/* ============================================================================
   CONFORMITÉ RÉGLEMENTAIRE d'une scène (3e banc, avec fidelity.js et legibility.js)
   ----------------------------------------------------------------------------
   La FIDÉLITÉ vérifie que la scène dit la même chose que le texte ; la
   LISIBILITÉ qu'elle est lisible ; ce module vérifie qu'elle est LÉGALE — que
   les placements respectent le règlement officiel de l'activité.

   RÈGLE PRODUIT : un placement doit être conforme au règlement, SAUF quand
   l'enseignant demande explicitement autre chose (exercice, situation
   d'apprentissage, organisation particulière). Chaque contrôle porte donc sa
   DÉROGATION : un motif de texte qui, s'il est présent, éteint le contrôle —
   c'est la demande explicite qui prime, jamais l'inverse.

   Volontairement CONSERVATEUR : on ne signale que des fautes NETTES, jamais
   une nuance d'appréciation. Renvoie [{code, msg}] — codes :
     - "service_serveur"  : le serveur n'engage pas d'où le règlement l'impose
                            (carré de service au badminton, derrière la ligne
                            de fond au tennis et au volley) ;
     - "service_chute"    : le service ne retombe pas dans la cible légale
                            (« rivière » au badminton, carré diagonal au tennis) ;
     - "service_receveur" : le relanceur n'est pas dans le carré de service
                            diagonal au moment du service (badminton, tennis) ;
     - "camp_filet"       : un joueur est dans le camp adverse (sports de filet) ;
     - "zone_gardien"     : un joueur de champ est DANS la surface de but
                            (handball, futsal) — seul le gardien y a droit ;
     - "sur_table"        : un joueur (ou une fin de course) est SUR la table
                            de tennis de table ;
     - "hors_table"       : une frappe de tennis de table retombe À CÔTÉ de la
                            table (typiquement visée sur le joueur d'en face).

   Géométrie : conventions de rendu de src/render.js (terrain 0..100, but
   attaqué à droite, filet en x=50).
   ========================================================================== */

/* Repères de terrain, en unités de scène (0..100) — miroir des tracés de
   render.js et de la table LM_SPORT de parser.js. */
const NET_COURTS = {
  // Sports de filet : ligne de service (courte), ligne de fond du court, et
  // couloir latéral de simple (hors couloirs de double).
  badminton: { net: 50, serviceLine: 34, courtBack: 0, sideMin: 7, sideMax: 93 },
  tennis: { net: 50, serviceLine: 28, courtBack: 10, sideMin: 11, sideMax: 89 },
  volleyball: { net: 50, serviceLine: null, courtBack: 10, sideMin: 0, sideMax: 100 },
  tennis_de_table: { net: 50, serviceLine: null, courtBack: 20, sideMin: 20, sideMax: 80 },
};
/* Surface de but (demi-ellipse autour de la cage) : demi-axes en x et en y,
   miroir de l'arc tracé par render.js (rayon 0,34 × hauteur du terrain). */
const GOAL_AREA = { handball: { ax: 20, ay: 34 }, futsal: { ax: 20, ay: 34 } };

/* DEUX FAMILLES DE DÉROGATION POUR LE FILET, AU NIVEAU MODULE — parce qu'elles
   n'ont pas la même PORTÉE (👎 PROD184/185) :
   • `DISPOSITIF_FILET` décrit TOUTE la scène : il n'y a pas de filet, pas de
     camps fixes, ou l'on change de côté exprès. Rien n'y est contrôlable.
   • `TOUCHE_POUR_SOI` décrit UNE touche de balle. Elle dit qu'une frappe peut
     rester d'un seul côté — elle ne dit RIEN sur le droit d'un joueur à
     traverser le filet.
   Les confondre a coûté deux 👎 d'affilée au même enseignant. */
const DISPOSITIF_FILET = /face au mur|contre le mur|au mur|sans filet|seul avec (?:sa raquette|son volant|sa balle)|passe sous le filet|de l'autre c[ôo]t[ée] du filet|change[nrz]* de c[ôo]t[ée]|rejoi\w+ (?:l'autre|la) (?:colonne|c[ôo]t[ée])|tournante|distributeur|\blanceur|panier de balles|multi.?balles?|atelier/;
const TOUCHE_POUR_SOI = /pour (?:soi|lui|elle)[- ]?m?[êe]?m?e?|jongl|touches? pour (?:soi|lui|elle)/;

/* DÉROGATIONS — motifs qui éteignent un contrôle parce que l'enseignant a
   explicitement demandé ce placement (c'est alors l'intention pédagogique). */
const WAIVERS = {
  service_serveur: /serv(?:e|ir|ice)[^.;]{0,40}(?:depuis|du) (?:le )?(?:fond|milieu|centre)|serveur[^.;]{0,30}(?:dans|sur) (?:le|la) (?:court|terrain)|service[^.;]{0,30}am[ée]nag|adapt[ée]/i,
  service_chute: /zone[s]? cible|cible au sol|carr[ée]s? trac[ée]s|casier|panier de balles|distributeur|lance le volant|envoie (?:la balle|le volant)/i,
  service_receveur: /couloir oppos[ée]|c[ôo]t[ée] oppos[ée]|mauvais c[ôo]t[ée]|d[ée]cal[ée]|du c[ôo]t[ée] inverse|volontairement|mal plac[ée]|part du fond|couloir de fond|fond de (?:court|terrain)/i,
  // NB : « observateur / arbitre / enseignant » ne figurent PLUS ici — un
  // encadrant est déjà exclu du contrôle des positions par isStaff(), et sa
  // seule mention dans le texte désarmait à tort la règle du FRANCHISSEMENT
  // (salve sans raisonnement : un smash volley dessiné vers son propre camp
  // passait la garde parce que « deux observateurs comptent les attaques »).
  // ⚠️ « \blanceur » et non « lanceur » : « re-LANCEUR » — le mot courant pour
  // le receveur aux raquettes — contenait la dérogation et ÉTEIGNAIT toute la
  // règle du camp. 👎 PROD61 : « le receveur fait son coup qui arrive dans son
  // propre terrain », scène acceptée par la garde parce que le texte disait
  // « le relanceur commence derrière la ligne de fond ». 21 prompts du lot
  // doré contiennent ce mot : le contrôle le plus important des sports de
  // filet était silencieux sur chacun d'eux.
  // ⚠️ MÊME PIÈGE QUE `\blanceur`, PAYÉ UNE 2e FOIS (👎 PROD184/185, 19/08) :
  // « une manchette POUR LUI MÊME, puis une passe POUR LUI MÊME » — vocabulaire
  // standard d'un exercice de volley — éteignait la famille camp_filet ENTIÈRE,
  // règles ET correcteurs. L'enseignant a redemandé sa scène en insistant, et a
  // reçu deux fois un joueur qui TRAVERSE le filet pour « se replacer au fond »
  // (« se replacer au fond de SON PROPRE terrain ! »).
  // La dérogation reste JUSTE pour les FRAPPES : au badminton, un jonglage EN
  // DÉPLACEMENT est un `renvoi` long d'un seul côté, parfaitement légitime
  // (test verrouillé, salve raquettes). Elle est ABSURDE pour les DÉPLACEMENTS :
  // une touche pour soi parle d'une touche de BALLE, jamais du droit de
  // traverser le filet. C'est sa PORTÉE qui était fausse, pas son existence —
  // voir `DISPOSITIF_FILET` ci-dessous, seul jeu de motifs qui décrit vraiment
  // toute la scène et qui gouverne désormais la règle 3bis et son correcteur.
  camp_filet: new RegExp(`${TOUCHE_POUR_SOI.source}|${DISPOSITIF_FILET.source}`, "i"),
  // « jets de 7 mètres / penalty » (salve handball nº1, H17) : l'Edge déployée
  // peut encore poser le tireur à l'ancienne convention x86 (dans l'ellipse) —
  // un exercice de jets ne doit pas partir au repli pour ça.
  zone_gardien: /dans la (?:zone|surface)|entre[rz]? dans la (?:zone|surface)|kung.?fu|gardien.?joueur|joueur.?gardien|sans gardien|but vide|au.?dessus de la zone|survol|jets? de 7|7 ?m[èe]tres|penalty/i,
  // Une balle volontairement jouée à côté de la table (faute travaillée, jeu
  // « en dehors », balle renvoyée à la main) reste la demande de l'enseignant.
  hors_table: /hors (?:de la )?table|à c[ôo]t[ée] de la table|balle out|sort de la table|faute (?:de service|volontaire)|sans table|au mur|contre le mur/i,
};
const waived = (code, text) => WAIVERS[code] ? WAIVERS[code].test(String(text || "")) : false;

const isKeeper = (p) => p.team === "k" || /gardien|goal|keeper/i.test(p.role || "");
// Même piège, plus grave encore : un joueur dont le RÔLE est « Relanceur »
// était classé ENCADRANT, donc exclu des contrôles de camp, de service et de
// position (👎 PROD61). Frontière de mot obligatoire.
const isStaff = (p) => p.team === "OBS" || /observateur|arbitre|coach|enseignant|professeur|distributeur|\blanceur|ramasseur/i.test(p.role || "");
const num = (v) => (Number.isFinite(+v) ? +v : null);

export function regulationIssues(scene, sport, text = "") {
  const issues = [];
  const push = (code, msg) => { if (!waived(code, text)) issues.push({ code, msg }); };
  const players = (Array.isArray(scene?.players) ? scene.players : [])
    .filter((p) => num(p.x) != null && num(p.y) != null);
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];
  if (!players.length) return issues;
  const byId = {}; players.forEach((p) => { byId[p.id] = p; });

  /* ── SPORTS DE FILET ────────────────────────────────────────────────────── */
  const court = NET_COURTS[sport];
  if (court) {
    // 1) CHACUN DANS SON CAMP : une équipe ne franchit pas le filet. On ne
    //    juge que les joueurs de terrain (l'encadrement se poste où il veut),
    //    et seulement si les deux camps sont peuplés (sinon c'est un atelier).
    const field = players.filter((p) => !isStaff(p) && (p.team === "A" || p.team === "B"));
    // Le camp d'une équipe n'existe que si elle y est FRANCHEMENT installée
    // (≥ 3/4 des siens). Une équipe répartie des deux côtés du filet n'est pas
    // une équipe qui triche : c'est un ATELIER (serveurs d'un côté, receveurs
    // de l'autre) — une organisation, donc hors du champ de ce contrôle.
    const campOf = (team) => {
      const mates = field.filter((p) => p.team === team);
      if (!mates.length) return null;
      const l = mates.filter((p) => num(p.x) < court.net).length;
      if (l / mates.length >= 0.75) return "left";
      if ((mates.length - l) / mates.length >= 0.75) return "right";
      return null;
    };
    const campA = campOf("A"), campB = campOf("B");
    if (campA && campB && campA !== campB) {
      for (const p of field) {
        const side = p.team === "A" ? campA : campB;
        const crossed = side === "left" ? num(p.x) > court.net + 2 : num(p.x) < court.net - 2;
        if (crossed) push("camp_filet", `${p.id} (équipe ${p.team}) est dans le camp adverse (x=${p.x})`);
      }
    }

    // 2) LE SERVICE : d'où il part, où il tombe, et où attend le relanceur.
    const strokes = actions.filter((a) => a.type !== "course").slice()
      .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
    const serve = strokes.find((a) => /^service|^mise en jeu|^engagement/i.test(a.label || ""));
    const server = serve && serve.from ? byId[serve.from] : null;
    if (serve && server) {
      const ownLeft = num(server.x) < court.net;
      // a) SERVEUR — badminton : dans son carré de service, donc DERRIÈRE la
      //    ligne de service court ; tennis/volley : derrière sa ligne de fond.
      if (sport === "badminton") {
        const inBox = ownLeft ? num(server.x) <= court.serviceLine : num(server.x) >= 100 - court.serviceLine;
        if (!inBox) push("service_serveur", `le serveur ${server.id} engage hors de son carré de service (x=${server.x})`);
      } else if (sport === "tennis" || sport === "volleyball") {
        const behind = ownLeft ? num(server.x) <= court.courtBack : num(server.x) >= 100 - court.courtBack;
        if (!behind) push("service_serveur", `le serveur ${server.id} engage dans le court, pas derrière sa ligne de fond (x=${server.x})`);
      }
      // b) CHUTE du service : dans le carré de service adverse.
      const tx = num(serve.tox), ty = num(serve.toy);
      if (tx != null) {
        if (sport === "badminton") {
          const legal = ownLeft ? tx >= 100 - court.serviceLine : tx <= court.serviceLine;
          if (!legal) push("service_chute", `le service retombe dans la « rivière » (x=${tx})`);
        } else if (sport === "tennis") {
          const legal = ownLeft ? (tx > court.net && tx <= 100 - court.serviceLine) : (tx < court.net && tx >= court.serviceLine);
          if (!legal) push("service_chute", `le service retombe hors du carré de service (x=${tx})`);
        }
        // b-bis) DIAGONALE (tennis ET badminton — retour prod 👎 : « le serveur
        //    est à droite et sert dans le mauvais carré !! ») : le contrôle
        //    ci-dessus ne jugeait que la PROFONDEUR (x) — jamais le côté. Le
        //    service croise : parti d'une moitié latérale franche, il retombe
        //    dans la moitié OPPOSÉE. Zones franches seulement (|y−50| > 4 des
        //    deux côtés) : un service axial reste hors du champ du contrôle.
        //    HORS terrains DUPLIQUÉS en bandes (« 3 terrains dans la largeur »,
        //    groupes _g2/_g3…) : chaque bande est un mini-terrain, la médiane
        //    y=50 n'y sépare plus les carrés de service.
        const multiCourts = players.some((p) => /_g\d/i.test(String(p.id)));
        if ((sport === "tennis" || sport === "badminton") && ty != null && !multiCourts) {
          const sy = num(server.y);
          if (sy != null && Math.abs(sy - 50) > 4 && Math.abs(ty - 50) > 4 && (sy - 50) * (ty - 50) > 0)
            push("service_chute", `le service ne croise pas la diagonale : servi depuis y=${sy}, il retombe du même côté (y=${ty}) — viser le carré diagonal opposé`);
        }
      }
      // c) RELANCEUR : dans le carré de service DIAGONAL, donc du côté visé par
      //    le service (badminton, tennis — le volley n'a pas de carré).
      // EN SIMPLE seulement : en double, deux joueurs se partagent le camp et
      // les formations (australienne, tandem) déplacent volontairement le
      // partenaire — désigner « le relanceur » n'a alors rien d'évident.
      const oppSide = players.filter((p) => !isStaff(p) && (num(p.x) < court.net) !== ownLeft);
      if ((sport === "badminton" || sport === "tennis") && ty != null && oppSide.length === 1) {
        const receiver = oppSide[0];
        if (receiver && Math.abs(ty - 50) > 4 && Math.abs(num(receiver.y) - 50) > 4
          && (num(receiver.y) - 50) * (ty - 50) < 0) {
          push("service_receveur", `le relanceur ${receiver.id} attend dans le mauvais carré de service (y=${receiver.y}, service→y=${ty})`);
        }
      }
    }
  }

  // 3) UNE FRAPPE D'ÉCHANGE FRANCHIT LE FILET (salve rallyes : smash adverse
  //    dessiné DANS son propre camp = contresens). Renvoi/tir long (≥12) dont
  //    départ ET arrivée sont du même côté du filet — les touches « pour soi »
  //    (courtes) sont exclues par la longueur. Même dérogation que camp_filet
  //    (ateliers, distributeurs, tournante…).
  if (court) {
    const byId2 = {}; players.forEach((p) => { byId2[p.id] = p; });
    // Au VOLLEY, réception et passe sont des « renvoi » qui restent dans son
    // camp (3 touches) : seule l'ATTAQUE (tir) doit franchir.
    // Au TENNIS DE TABLE non plus : le SERVICE rebondit d'abord dans SA PROPRE
    // demi-table avant de franchir le filet — une remise dessinée à x=30 par un
    // joueur de gauche est le premier rebond réglementaire, pas une faute
    // (salve raquettes : la contre-salve a buté là-dessus, l'IA corrigeant une
    // frappe hors table en la ramenant sur sa demi-table, légalement). Ce que
    // le rectangle de `hors_table` ne couvre pas, c'est l'ATTAQUE conclue dans
    // son propre camp : `tir` reste donc contrôlé.
    // Au badminton et au tennis, la balle ne rebondit pas côté frappeur : toute
    // frappe longue franchit.
    const mustCross = (sport === "volleyball" || sport === "tennis_de_table")
      ? new Set(["tir"]) : new Set(["tir", "renvoi"]);
    for (const a of actions) {
      if (!mustCross.has(a.type)) continue;
      const from = a.fromx != null ? { x: +a.fromx } : byId2[a.from];
      const tx = a.to != null ? num(byId2[a.to]?.x) : num(a.tox);
      if (!from || tx == null) continue;
      const fx = num(from.x);
      const len = Math.abs(tx - fx);
      // Le seuil de longueur n'existe que pour épargner les touches « pour
      // soi » du VOLLEY (réception/passe dans son camp, 3 touches). Aux sports
      // de RAQUETTE, il n'y a pas de touche pour soi : une frappe qui ne
      // franchit pas est fautive dès qu'elle est visible. À 12, une « volée
      // gagnante » dessinée à 10 unités VERS SON PROPRE FOND passait (salve
      // raquettes, R25).
      if (len < (sport === "volleyball" ? 12 : 6)) continue;
      if ((fx - court.net) * (tx - court.net) > 0 && Math.abs(fx - court.net) > 2 && Math.abs(tx - court.net) > 2)
        push("camp_filet", `la frappe ${a.order ?? ""} (${a.type}) ne franchit pas le filet (x ${Math.round(fx)}→${Math.round(tx)})`);
    }

    // DIAGONALE IMPOSSIBLE AU BADMINTON (👎 prod 10/08 : « Le service doit être
    // dans la diagonale !!! ici placement des joueurs ET direction du service
    // sont faux »). Le contrôle `service_chute` juge la RETOMBÉE ; il ne dit
    // rien quand serveur et receveur sont du MÊME côté de la ligne centrale —
    // ou tous deux dessus, comme dans le cas signalé (les deux en y=50). Aucune
    // diagonale n'est alors dessinable : le défaut est dans le PLACEMENT.
    // Simple uniquement (en double les quatre joueurs occupent les deux
    // demi-courts, la notion n'a pas de sens).
    // Mesuré : 2 déclenchements sur 836, TOUS DEUX des 👎 archivés.
    // Deux garde-fous repris de `service_receveur`, que les tests unitaires ont
    // rappelés à l'ordre : les TERRAINS DUPLIQUÉS en bandes (chaque bande est
    // un mini-terrain, y=50 n'y sépare plus rien) et la DÉROGATION de texte
    // (« le joueur B démarre du couloir opposé » : l'enseignant l'a voulu).
    const bandes = players.some((p) => /_g\d/i.test(String(p.id)));
    if (sport === "badminton" && !bandes && !waived("service_receveur", text)
      && !/doubles?|mixte/i.test(String(text || ""))) {
      const srv = actions.find((a) => /service|engage/i.test(String(a.label || "")));
      const s = srv && byId2[srv.from];
      if (s) {
        const rec = players.find((p) => p.id !== s.id && !isStaff(p) && !isKeeper(p)
          && (num(p.x) - court.net) * (num(s.x) - court.net) < 0);
        if (rec && (num(s.y) - 50) * (num(rec.y) - 50) >= 0)
          push("service_diagonale", `serveur (${s.id}, y=${Math.round(num(s.y))}) et relanceur (${rec.id}, y=${Math.round(num(rec.y))}) sont du MÊME côté de la ligne centrale : le service en diagonale est impossible à dessiner — les placer dans des demi-courts OPPOSÉS (l'un en y<50, l'autre en y>50)`);
      }
    }

    // SERVICE AU TENNIS DE TABLE — DEUX REBONDS (👎 prod 09/08 : « c'est quoi
    // ces services ?? un service au tennis de table rebondit sur la table du
    // serveur ET de l'adversaire ; ici les services n'ont qu'un rebond sur la
    // table du serveur puis arrivent dehors »).
    // Le `renvoi` est HORS de `mustCross` au TT, précisément parce que le
    // service touche d'abord SA PROPRE demi-table — mais cette dérogation
    // laissait passer un service qui s'y ARRÊTE. On sépare les deux choses :
    // la scène porte la chute FINALE, qui doit être dans la demi-table
    // ADVERSE ; le premier rebond, côté serveur, est dérivé au RENDU
    // (autoRebondPoints) — l'IA n'a pas à le placer.
    // Mesuré avant écriture : 0 déclenchement sur les 40 services de TT du lot
    // doré (le moteur les envoie déjà du bon côté) ; attrape le 👎 PROD16.
    if (sport === "tennis_de_table") {
      for (const a of actions) {
        if (a.type !== "renvoi" && a.type !== "tir") continue;
        if (!/service|engage/i.test(String(a.label || ""))) continue;
        const from = a.fromx != null ? { x: +a.fromx } : byId2[a.from];
        const tx = a.to != null ? num(byId2[a.to]?.x) : num(a.tox);
        if (!from || tx == null) continue;
        const fx = num(from.x);
        if ((fx - court.net) * (tx - court.net) > 0)
          push("service_deux_rebonds", `le service ${a.order ?? ""} retombe dans la demi-table du SERVEUR (x ${Math.round(fx)}→${Math.round(tx)}) : au tennis de table il rebondit d'abord chez le serveur PUIS chez l'adversaire — viser la demi-table ADVERSE (x ${fx < court.net ? "entre 52 et 76" : "entre 24 et 48"}), le premier rebond est dessiné automatiquement`);
      }
    }
  }

  /* ── 3bis) UNE COURSE NE TRAVERSE PAS LE FILET (👎 prod : « les déplacements
     des joueurs traversent le filet !! ») — un joueur de champ court DANS le
     camp adverse : départ (position évoluée) et arrivée de part et d'autre du
     filet, ou arrivée franchement chez l'adversaire.
     ⚠️ DÉROGATIONS DE DISPOSITIF SEULEMENT (tournante, changement de côté,
     ateliers…) — surtout PAS les touches « pour soi », qui ne parlent que du
     ballon. `pushC` court-circuite donc `push`, dont la dérogation est celle,
     plus large, du code `camp_filet` (👎 PROD184/185 : « le joueur doit se
     replacer au fond de SON PROPRE terrain ! »). */
  if (court && !DISPOSITIF_FILET.test(String(text || ""))) {
    const pushC = (code, msg) => issues.push({ code, msg });
    // Comme pour la règle 1 : les CAMPS doivent exister (≥ 3/4 de l'équipe du
    // coureur installée d'un côté). Une équipe répartie des deux côtés est un
    // ATELIER (étoile, rotation…) — traverser la médiane y est normal.
    const sideOf = (team) => {
      // Un joueur posé SUR la ligne du filet (|x-50|<2) n'appartient à aucun
      // camp : l'exclure du vote — deux contreurs à x=50 exactement donnaient
      // « équipe à DROITE » (50 < 50 est faux) et empoisonnaient l'inférence
      // (salve volley V02, chantier du contre).
      const mates = players.filter((p) => !isStaff(p) && !isKeeper(p) && p.team === team
        && Math.abs(num(p.x) - court.net) >= 2);
      if (!mates.length) return null;
      const l = mates.filter((p) => num(p.x) < court.net).length;
      return l / mates.length >= 0.75 ? "left" : (mates.length - l) / mates.length >= 0.75 ? "right" : null;
    };
    // Et il faut un ADVERSAIRE : un joueur seul (étoile, navette, jonglage) n'a
    // ni camp ni filet à respecter — c'est un drill de déplacement.
    const camps = { A: sideOf("A"), B: sideOf("B") };
    if (!camps.A || !camps.B) camps.A = camps.B = null;
    // JOUEUR POSÉ SUR LE FILET — promotion RÈGLEMENT pour le VOLLEY (salve
    // volley nº2, V02 : les deux contreurs à x=50,0 pile, puis leurs courses
    // de contre dans le camp adverse — et l'inférence de camp empoisonnée se
    // taisait). La version LISIBILITÉ (soft) reste pour les autres sports de
    // filet. Mesuré sur les 38 scènes volley archivées : 6 déclenchements,
    // TOUS sur des 👎 réels (PROD5, PROD78) ou la salve — 0 scène saine.
    if (sport === "volleyball") {
      for (const p of players) {
        if (isStaff(p) || isKeeper(p) || (p.team !== "A" && p.team !== "B")) continue;
        if (Math.abs(num(p.x) - court.net) < 2)
          push("sur_filet", `${p.id} se tient SUR le filet (x=${Math.round(num(p.x))}) — un joueur de volley reste DANS son camp : placer le contreur à une longueur de bras du filet (x≈46 à gauche, x≈54 à droite)`);
      }
    }
    const posC = {};
    for (const p of players) posC[p.id] = num(p.x);
    const orderedC = actions.filter((a) => a.order != null).slice().sort((a, b) => a.order - b.order);
    for (const a of [...orderedC, ...actions.filter((x) => x.order == null)]) {
      if (a.type !== "course" && a.type !== "dribble") continue;
      const p = byId[a.from];
      if (!p || isStaff(p) || isKeeper(p) || !(p.team === "A" || p.team === "B")) continue;
      const fx = Number.isFinite(+a.fromx) ? +a.fromx : posC[a.from];
      const tx = num(a.tox);
      if (fx == null || tx == null) continue;
      const camp = camps[p.team];
      if (!camp) { posC[a.from] = tx; continue; }   // pas de camp franc : atelier
      const crosses = (fx - court.net) * (tx - court.net) < 0 && Math.abs(tx - court.net) > 3;
      const landsOpposite = camp === "left" ? tx > court.net + 3 : tx < court.net - 3;
      // TROU DE LA RÈGLE (salve raquettes) : les deux tests ci-dessus laissent
      // passer l'arrivée EXACTEMENT sur le filet (x=50) — ni franchissement, ni
      // camp adverse. C'est pourtant le cas le plus fréquent (« Montée au
      // filet » et « Replacement au centre » se terminent en x=50, 4 scènes
      // sur 30) : le joueur finit DANS le filet, et on ne sait plus dans quel
      // camp il joue. Bande étroite : x=48 ou 52 reste toléré comme avant.
      const onNet = Math.abs(tx - court.net) < 2;
      if (crosses || landsOpposite)
        pushC("camp_filet", `la course ${a.order ?? ""} de ${a.from} traverse le filet (x ${Math.round(fx)}→${Math.round(tx)}) — un joueur reste dans son camp`);
      else if (onNet)
        pushC("camp_filet", `la course ${a.order ?? ""} de ${a.from} s'arrête SUR le filet (x=${Math.round(tx)}) — monter au filet, c'est s'arrêter AVANT : viser x≈${camp === "left" ? 43 : 57} pour rester dans son camp`);
      posC[a.from] = tx;   // suivi : la course déplace son acteur
    }
  }

  /* ── 3ter) TENNIS DE TABLE : PERSONNE SUR LA TABLE (👎 prod ×2 : « un joueur
     est sur la table !! les déplacements sont sur la table !! c'est du TENNIS
     DE TABLE ! ») — la table (x 22-78 · y 20-80, miroir du tracé render.js,
     dont le commentaire dit lui-même « les joueurs évoluent À L'EXTÉRIEUR »)
     n'est pas un terrain : aucun joueur debout dessus, aucune course qui s'y
     termine. Pas de dérogation possible — on ne monte pas sur une table.
     HORS tables dupliquées (_g2…), où la géométrie par bande diffère. */
  if (sport === "tennis_de_table" && !players.some((p) => /_g\d/i.test(String(p.id)))) {
    const onTable = (x, y) => x != null && y != null && x > 23 && x < 77 && y > 21 && y < 79;
    for (const p of players) {
      if (isStaff(p) || isKeeper(p)) continue;
      if (onTable(num(p.x), num(p.y)))
        push("sur_table", `${p.id} est debout SUR la table (x=${p.x}, y=${p.y}) — un joueur se place À L'EXTÉRIEUR, derrière sa demi-table (x<20 ou x>80)`);
    }
    for (const a of actions) {
      if (a.type !== "course" && a.type !== "dribble") continue;
      const p = byId[a.from];
      if (!p || isStaff(p)) continue;
      if (onTable(num(a.tox), num(a.toy)))
        push("sur_table", `le déplacement ${a.order ?? ""} de ${a.from} se termine SUR la table (${Math.round(+a.tox)},${Math.round(+a.toy)}) — les déplacements se font AUTOUR de la table`);
    }

    /* ── 3quater) … ET LA BALLE, ELLE, RETOMBE DESSUS (revers du même 👎 :
       « pas d'impacts de rebond ») — le pendant exact de sur_table. Les
       joueurs se tenant À L'EXTÉRIEUR de la table, une frappe dirigée vers
       l'ADVERSAIRE (« to » : son pion) ou vers un point hors du rectangle
       envoie la balle à côté de la table : elle ne rebondit nulle part, et
       `autoRebondPoints` (render.js) n'a alors aucun impact à dessiner — le
       correctif de rendu reste muet faute de retombée légale. Salve
       raquettes : 5 scènes sur 30 ; attrape aussi le 👎 archivé PROD39. */
    // Rectangle TOLÉRANT (l'inverse de `onTable`, qui rétrécit pour ne dénoncer
    // qu'un joueur franchement monté dessus) : une balle qui retombe SUR la
    // ligne est bonne — on ne signale que ce qui tombe nettement à côté.
    const ballOnTable = (x, y) => x >= 21 && x <= 79 && y >= 19 && y <= 81;
    for (const a of actions) {
      if (a.type !== "renvoi" && a.type !== "tir") continue;
      const target = a.to != null ? byId[a.to] : null;
      const tx = target ? num(target.x) : num(a.tox);
      const ty = target ? num(target.y) : num(a.toy);
      if (tx == null || ty == null) continue;
      if (!ballOnTable(tx, ty))
        push("hors_table", `la frappe ${a.order ?? ""} (${a.label || a.type}) retombe hors de la table (${Math.round(tx)},${Math.round(ty)}) — viser un POINT de la demi-table adverse (x entre 24 et 76, y entre 22 et 78), pas le joueur d'en face`);
    }
  }

  /* ── SURFACE DE BUT (handball, futsal) ──────────────────────────────────── */
  const area = GOAL_AREA[sport];
  if (area) {
    for (const p of players) {
      if (isKeeper(p) || isStaff(p)) continue;
      const x = num(p.x), y = num(p.y);
      for (const gx of [0, 100]) {
        const d = ((x - gx) / area.ax) ** 2 + ((y - 50) / area.ay) ** 2;
        // ⚠️ LA MARGE ÉTAIT À 0,8 — « un pivot POSÉ SUR la ligne reste légal ».
        // DEUX enseignants disent le contraire, dans ces termes : « les
        // défenseurs sont sur la ligne des 6 mètres, C'EST INTERDIT » (👎 PROD6)
        // et « joueur 1 dans la zone !! » (👎 PROD189, joueur à d = 1,000 pile).
        // Ils ont raison au règlement : la ligne de surface APPARTIENT à la
        // surface. Le seuil est donc l'arc lui-même (d ≤ 1), et non plus une
        // tolérance calibrée sur une hypothèse que le terrain dément.
        // Mesuré sur 415 joueurs de champ / 72 scènes handball-futsal : 12
        // joueurs sont pile sur la ligne, dont 6 sur des 👎 réels. Bancs après
        // changement : règlement 1161/1162 et fidélité 158/158 INCHANGÉS,
        // lisibilité 1068 -> 1069 (le correcteur desserre une grappe). Le
        // correcteur ne pousse que d'UNE unité : rien ne se réorganise.
        if (d <= 1) push("zone_gardien", `${p.id} est dans la surface de but (x=${p.x}, y=${p.y})`);
      }
    }
    // …ET LES ARRIVÉES DE COURSE (👎 prod 10/08 : « l'action se déroule dans la
    // zone des 6 m, complètement INTERDITE aux joueurs de champ !! nul »). Le
    // contrôle ci-dessus ne regardait que les positions de DÉPART : un joueur
    // posté hors surface qui y dribble ensuite passait sans un mot. Un TIR est
    // exclu — c'est le ballon qui entre dans le but, pas le joueur.
    // HANDBALL seulement : au futsal le pivot joue au ras de la surface et le
    // gardien-joueur y entre, la mesure y donnait des faux positifs en série.
    // Mesuré : 4 déclenchements sur les 836 scènes du lot doré, dont 2 sur des
    // 👎 archivés (PROD47, PROD69) ; les 2 autres sont un croisé de handball
    // qui finit effectivement dans la surface.
    if (sport === "handball") {
      const byId3 = {}; players.forEach((p) => { byId3[p.id] = p; });
      for (const a of actions) {
        if (a.type !== "course" && a.type !== "dribble") continue;
        const p = byId3[a.from];
        if (!p || isKeeper(p) || isStaff(p)) continue;
        const x = num(a.tox), y = num(a.toy);
        if (x == null || y == null) continue;
        for (const gx of [0, 100]) {
          if (((x - gx) / area.ax) ** 2 + ((y - 50) / area.ay) ** 2 < 0.8)
            push("zone_gardien", `le déplacement ${a.order ?? ""} de ${a.from} FINIT dans la surface de but (${Math.round(x)},${Math.round(y)}) — interdite aux joueurs de champ : s'arrêter devant l'arc, le tir part de là`);
        }
      }
    }
  }

  return issues;
}

/* ────────────────────────────────────────────────────────────────────────────
   MISE EN CONFORMITÉ (contrepartie corrective de regulationIssues)
   Applique au RENDU les placements que le règlement impose, sans rien
   restructurer : on NUDGE le joueur au plus court, en gardant ce qui porte le
   sens tactique (sa hauteur sur le terrain, son côté). Les dérogations valent
   ici aussi : une demande explicite de l'enseignant n'est jamais écrasée.
   Mute la scène et la renvoie. */
export function enforceRegulation(scene, sport, text = "") {
  const players = (Array.isArray(scene?.players) ? scene.players : [])
    .filter((p) => num(p.x) != null && num(p.y) != null);
  if (!players.length) return scene;

  // 1) SURFACE DE BUT : aucun joueur de champ dedans (le gardien y est chez lui).
  const area = GOAL_AREA[sport];
  if (area && !waived("zone_gardien", text)) {
    // Abscisse LÉGALE la plus proche du but, à une hauteur donnée (bord de l'arc).
    const edge = (gx, y) => {
      const dy = Math.min(0.98, ((y - 50) / area.ay) ** 2);
      const need = area.ax * Math.sqrt(Math.max(0, 1 - dy));
      return gx === 0 ? Math.min(97, Math.round(need + 1)) : Math.max(3, Math.round(100 - need - 1));
    };
    // MÊME SEUIL QUE SA RÈGLE, littéralement : il valait 0,95 quand la règle
    // valait 0,8 — un correcteur qui déplace ce que sa règle ne signale pas est
    // le même désalignement que l'inverse, en plus discret. Les deux tiennent
    // désormais l'arc (d ≤ 1), et `edge()` reloge à UNE unité au-delà.
    const inside = (p, gx) => ((num(p.x) - gx) / area.ax) ** 2 + ((num(p.y) - 50) / area.ay) ** 2 <= 1;
    const moved = [];
    for (const p of players) {
      if (isKeeper(p) || isStaff(p)) continue;
      for (const gx of [0, 100]) {
        if (!inside(p, gx)) continue;
        // On sort par le plus court chemin en x : la hauteur (aile / axe) porte
        // le sens tactique, elle est conservée.
        p.x = edge(gx, num(p.y));
        moved.push({ p, gx });
      }
    }
    // Les sortants se retrouvent tous sur le bord de l'arc : on les DÉSEMPILE en
    // hauteur (le bord se recalcule à la nouvelle hauteur, donc on reste légal).
    for (const { p, gx } of moved) {
      const clash = () => players.some((q) => q !== p && Math.hypot(num(q.x) - num(p.x), num(q.y) - num(p.y)) < 5);
      if (!clash()) continue;
      const y0 = num(p.y);
      for (const dy of [7, -7, 14, -14, 21, -21]) {
        const y = Math.max(6, Math.min(94, y0 + dy));
        p.y = y; p.x = edge(gx, y);
        if (!clash()) break;
      }
    }
    // Les ARRIVÉES de course/dribble sortent elles aussi (👎 prod 10/08) :
    // sinon le contrôle signalerait un défaut que la mise en conformité ne
    // sait pas réparer, et le corpus deviendrait durablement non conforme.
    // Handball seulement, comme le contrôle. Le TIR est épargné : sa cible EST
    // le but.
    if (sport === "handball") {
      const byIdE = {}; players.forEach((p) => { byIdE[p.id] = p; });
      for (const a of (Array.isArray(scene?.actions) ? scene.actions : [])) {
        if (a.type !== "course" && a.type !== "dribble") continue;
        const p = byIdE[a.from];
        if (!p || isKeeper(p) || isStaff(p)) continue;
        const x = num(a.tox), y = num(a.toy);
        if (x == null || y == null) continue;
        for (const gx of [0, 100]) {
          if (((x - gx) / area.ax) ** 2 + ((y - 50) / area.ay) ** 2 < 0.95) a.tox = edge(gx, y);
        }
      }
    }
  }

  // 2) SERVICE : le serveur engage d'où le règlement l'impose.
  const court = NET_COURTS[sport];
  if (court && !waived("service_serveur", text)) {
    const byId = {}; players.forEach((p) => { byId[p.id] = p; });
    const serve = (Array.isArray(scene?.actions) ? scene.actions : [])
      .filter((a) => a.type !== "course").slice()
      .sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
      .find((a) => /^service|^mise en jeu|^engagement/i.test(a.label || ""));
    const server = serve && serve.from ? byId[serve.from] : null;
    if (server) {
      const ownLeft = num(server.x) < court.net;
      // DIAGONALE DESSINABLE (salve badminton nº3, B06) : le rally du MOTEUR
      // pose serveur et relanceur PILE sur la ligne centrale (y=50/y=50) — la
      // règle `service_diagonale` du 10/08 refuse alors la scène. On ne
      // corrige QUE ce cas sûr (les deux sur l'axe, simple, hors bandes) : le
      // serveur passe en y=35 de son côté, le relanceur en y=65, la diagonale
      // redevient dessinable. Les placements IA délibérés (même côté mais
      // francs) restent signalés sans être déplacés — la réparation IA s'en
      // charge sur la route scène.
      if (sport === "badminton" && !/doubles?|mixte/i.test(String(text || ""))
        && !players.some((p) => /_g\d/i.test(String(p.id)))) {
        const rec = players.find((p) => p !== server && !isStaff(p) && !isKeeper(p)
          && (num(p.x) - court.net) * (num(server.x) - court.net) < 0);
        if (rec && Math.abs(num(server.y) - 50) < 4 && Math.abs(num(rec.y) - 50) < 4) {
          server.y = 35; rec.y = 65;
          // La chute du service suit la diagonale si elle était sur l'axe.
          if (Number.isFinite(+serve.toy) && Math.abs(+serve.toy - 50) < 4) serve.toy = 65;
        }
      }
      if (sport === "badminton") {
        if (ownLeft && num(server.x) > court.serviceLine) server.x = court.serviceLine - 4;
        if (!ownLeft && num(server.x) < 100 - court.serviceLine) server.x = 100 - court.serviceLine + 4;
      } else if (sport === "tennis" || sport === "volleyball") {
        if (ownLeft && num(server.x) > court.courtBack) server.x = Math.max(3, court.courtBack - 5);
        if (!ownLeft && num(server.x) < 100 - court.courtBack) server.x = Math.min(97, 100 - court.courtBack + 5);
      }
    }
  }

  /* 3) LA FRAPPE QUI NE FRANCHIT PAS LE FILET — doyenne des classes de rejet,
     elle résiste depuis quatre rejeux malgré des consignes chiffrées dans le
     prompt. Le contrôle `camp_filet` la signale ; il n'y avait personne pour
     la corriger. Or la correction est déterminée : la retombée est du MAUVAIS
     CÔTÉ du filet, on la MIROITE. Ce faisant on préserve exactement la
     profondeur choisie par le modèle — un dégagement reste long, un amorti
     reste court — et on ne change que le camp, qui est la faute.
     Jamais appliqué quand la frappe vise un JOUEUR (`a.to`) : il faudrait
     déplacer ce joueur, ce que cette fonction ne s'autorise pas. */
  if (court && !waived("camp_filet", text)) {
    const byId3 = {}; players.forEach((p) => { byId3[p.id] = p; });
    const mustCross = (sport === "volleyball" || sport === "tennis_de_table")
      ? new Set(["tir"]) : new Set(["tir", "renvoi"]);
    for (const a of (Array.isArray(scene?.actions) ? scene.actions : [])) {
      if (!mustCross.has(a.type) || a.to != null || num(a.tox) == null) continue;
      const from = a.fromx != null ? { x: num(a.fromx) } : byId3[a.from];
      if (!from || num(from.x) == null) continue;
      const fx = num(from.x), tx = num(a.tox);
      if (Math.abs(tx - fx) < (sport === "volleyball" ? 12 : 6)) continue;
      if (!((fx - court.net) * (tx - court.net) > 0 && Math.abs(fx - court.net) > 2 && Math.abs(tx - court.net) > 2)) continue;
      const profondeur = Math.abs(tx - court.net);
      const cible = fx < court.net ? court.net + profondeur : court.net - profondeur;
      a.tox = Math.round(Math.max(court.courtBack + 2, Math.min(98 - court.courtBack, cible)));
    }
  }

  /* 4) LA COURSE QUI TRAVERSE LE FILET — `enforceRegulation` savait déjà
     ramener une POSITION dans sa zone légale, jamais une ARRIVÉE de course.
     Un joueur reste dans son camp : on ramène l'arrivée juste en deçà du
     filet, du côté d'où il part. La hauteur (y) est conservée — c'est elle qui
     porte le sens tactique (montée au filet côté revers, replacement centre).
     Même portée de dérogation que la règle 3bis qu'il sert : le DISPOSITIF
     seul. Un correcteur qui s'abstient là où sa règle accuse est la faute (A)
     du dépôt, et elle a déjà été payée huit fois. */
  if (court && !DISPOSITIF_FILET.test(String(text || ""))) {
    const byId4 = {}; players.forEach((p) => { byId4[p.id] = p; });
    for (const a of (Array.isArray(scene?.actions) ? scene.actions : [])) {
      if (a.type !== "course" || a.to != null || num(a.tox) == null) continue;
      const acteur = byId4[a.from];
      const fx = a.fromx != null ? num(a.fromx) : (acteur ? num(acteur.x) : null);
      if (fx == null || Math.abs(fx - court.net) < 3) continue;   // départ ambigu : on ne tranche pas
      const tx = num(a.tox);
      const gauche = fx < court.net;
      const fautif = gauche ? tx > court.net - 2 : tx < court.net + 2;
      if (!fautif) continue;
      a.tox = gauche ? court.net - 4 : court.net + 4;
    }
  }
  return scene;
}
